(() => {
  const REQUEST_TIMEOUT_MS = 8000;
  const DOCUMENT_RULES = {
    dni: { min: 8, max: 8, api: true, inputMode: 'numeric' },
    ruc: { min: 11, max: 11, api: true, inputMode: 'numeric' },
    ce: { min: 4, max: 12, api: false, inputMode: 'text' },
    ptp: { min: 4, max: 12, api: false, inputMode: 'text' },
  };
  const DEBUG_QUERY_PARAM = 'identity_debug';
  const STORAGE_KEY = 'korean_cosmetics_identity_v1';
  const LEGACY_STORAGE_VERSION = 1;
  const STORAGE_VERSION = 3;
  const ACTIVE_ATTRIBUTE_NAMES = {
    document_type: 'Tipo de documento',
    document_number: 'Número de documento',
    document_validated: 'Documento validado por API',
    document_unvalidated_reason: 'Motivo por cual no fue validado',
    location_department: 'Departamento',
    location_province: 'Provincia',
    location_district: 'Distrito',
    location_ubigeo: 'UBIGEO',
  };
  const LEGACY_ATTRIBUTE_KEYS = [
    'document_type', 'document_number', 'document_validated', 'document_unvalidated_reason',
    'location_department', 'location_province', 'location_district', 'location_ubigeo',
    'validation_debug', 'validation_debug_scenario', 'document_declared',
    'document_dni', 'document_ruc', 'document_ce', 'document_ptp', 'document_full_name',
    'document_first_names', 'document_paternal_surname',
    'document_maternal_surname', 'document_last_names',
    'document_business_name', 'document_tax_status',
    'document_tax_condition', 'document_tax_ubigeo',
    'document_address', 'document_address_road_type',
    'document_address_road_name', 'document_address_number',
    'document_address_district', 'document_address_province',
    'document_address_department', 'document_source',
    'document_provider_code', 'document_provider_message',
    'document_fallback', 'document_fallback_reason',
    'location_inei_code', 'location_provider_id', 'location_validated',
    'location_fallback', 'location_fallback_reason', 'location_source',
    'Tipo de documento', 'Número de documento', 'Documento validado por API',
    'Motivo por cual no fue validado', 'Departamento', 'Provincia', 'Distrito', 'UBIGEO'
  ];

  let wrapper = null;
  let elements = null;
  let i18n = {};
  let memoryStorage = null;

  const state = {
    initialized: false,
    activeForm: null,
    activeButton: null,
    bypassForm: null,
    lastFocused: null,
    view: 'form',
    debugEnabled: false,
    debugScenario: 'both',
    documentKey: '',
    documentPayload: null,
    documentValidated: false,
    documentDeclared: false,
    documentFallbackAllowed: false,
    documentFallbackReason: '',
    documentProviderCode: '',
    documentProviderMessage: '',
    locationMode: 'idle',
    locationCatalog: [],
    locationCatalogSource: '',
    locationSelection: null,
    locationFallbackReason: '',
    locationRequestId: 0,
    storedRecord: null,
    busy: false,
  };

  function getElements() {
    return {
      modal: wrapper.querySelector('[data-identity-modal]'),
      closeButtons: wrapper.querySelectorAll('[data-identity-close]'),
      summary: wrapper.querySelector('[data-identity-summary]'),
      form: wrapper.querySelector('[data-identity-form]'),
      summaryGreeting: wrapper.querySelector('[data-summary-greeting]'),
      summaryDocumentTitle: wrapper.querySelector('[data-summary-document-title]'),
      summaryDocumentDetail: wrapper.querySelector('[data-summary-document-detail]'),
      summaryAddressRow: wrapper.querySelector('[data-summary-address-row]'),
      summaryAddress: wrapper.querySelector('[data-summary-address]'),
      summaryLocationCard: wrapper.querySelector('[data-summary-location-card]'),
      summaryLocation: wrapper.querySelector('[data-summary-location]'),
      summaryUbigeo: wrapper.querySelector('[data-summary-ubigeo]'),
      summaryResult: wrapper.querySelector('[data-summary-result]'),
      editButton: wrapper.querySelector('[data-identity-edit]'),
      forgetButtons: wrapper.querySelectorAll('[data-identity-forget]'),
      continueButtons: wrapper.querySelectorAll('[data-identity-continue]'),
      docType: wrapper.querySelector('[data-document-type]'),
      docNumber: wrapper.querySelector('[data-document-number]'),
      docNumberLabel: wrapper.querySelector('[data-document-number-label]'),
      validateButton: wrapper.querySelector('[data-document-validate]'),
      documentResult: wrapper.querySelector('[data-document-result]'),
      debugPanel: wrapper.querySelector('[data-identity-debug-panel]'),
      debugEnabled: wrapper.querySelector('[data-identity-debug-enabled]'),
      debugScenario: wrapper.querySelector('[data-identity-debug-scenario]'),
      locationContainer: wrapper.querySelector('[data-location-container]'),
      department: wrapper.querySelector('[data-location-department]'),
      province: wrapper.querySelector('[data-location-province]'),
      district: wrapper.querySelector('[data-location-district]'),
      locationSelects: wrapper.querySelector('[data-location-selects]'),
      locationSaved: wrapper.querySelector('[data-location-saved]'),
      locationSavedText: wrapper.querySelector('[data-location-saved-text]'),
      changeLocationButton: wrapper.querySelector('[data-location-change]'),
      ubigeo: wrapper.querySelector('[data-location-ubigeo]'),
      locationResult: wrapper.querySelector('[data-location-result]'),
    };
  }

  function parseI18n() {
    try {
      return JSON.parse(wrapper.querySelector('[data-identity-i18n]')?.textContent || '{}');
    } catch (_error) {
      return {};
    }
  }

  function sanitizeDigits(value) {
    return String(value || '').replace(/\D/g, '');
  }

  function sanitizeDeclaredDocument(value) {
    return String(value || '').toUpperCase();
  }

  function sanitizeDocumentNumber(type, value) {
    return DOCUMENT_RULES[type]?.api ? sanitizeDigits(value) : sanitizeDeclaredDocument(value);
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function normalizeAddress(value) {
    return String(value || '')
      .split(',')
      .map((part) => part.trim().replace(/\s+/g, ' '))
      .filter((part) => part && /[A-Za-z\u00C0-\u024F0-9]/.test(part) && !/^(null|undefined|-+)$/i.test(part))
      .filter((part, index, values) => values.indexOf(part) === index)
      .join(', ');
  }

  function interpolate(template, values = {}) {
    return Object.entries(values).reduce(
      (message, [key, value]) => message
        .replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), value)
        .replace(new RegExp(`__${key.toUpperCase()}__`, 'g'), value),
      template || ''
    );
  }

  function setResult(element, status, html) {
    if (!element) return;
    element.classList.remove('is-idle', 'is-loading', 'is-success', 'is-error', 'is-fallback');
    if (status) element.classList.add(`is-${status}`);
    element.innerHTML = html || '';
  }

  function setBusy(busy) {
    state.busy = busy;
    elements.continueButtons.forEach((button) => { button.disabled = busy; });
    elements.editButton.disabled = busy;
    elements.forgetButtons.forEach((button) => { button.disabled = busy; });
    if (elements.changeLocationButton) elements.changeLocationButton.disabled = busy;
    if (elements.validateButton) elements.validateButton.disabled = busy;
    if (elements.docType) elements.docType.disabled = busy;
    if (elements.docNumber) elements.docNumber.disabled = busy;
  }

  async function fetchWithTimeout(url) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      return await fetch(url, { method: 'GET', signal: controller.signal });
    } finally {
      clearTimeout(timeout);
    }
  }

  function getDocumentType() {
    const type = String(elements.docType?.value || 'dni').toLowerCase();
    return DOCUMENT_RULES[type] ? type : 'dni';
  }

  function getDocumentKey(type, number) {
    return `${type}:${number}`;
  }

  function parseBooleanDataAttribute(name, fallback = true) {
    const value = wrapper?.dataset?.[name];
    if (value === undefined || value === '') return fallback;
    return value !== 'false';
  }

  function isDocumentApiEnabled() {
    return parseBooleanDataAttribute('documentApiEnabled', true);
  }

  function isUbigeoEnabled() {
    return parseBooleanDataAttribute('ubigeoEnabled', true);
  }

  function usesDocumentApi(type) {
    return isDocumentApiEnabled() && Boolean(DOCUMENT_RULES[type]?.api);
  }

  function isDeclaredPayload(payload) {
    return payload?.declared === true || payload?.data?.fuente === 'customer_declared';
  }

  function isDebugAvailable() {
    return new URLSearchParams(window.location.search).get(DEBUG_QUERY_PARAM) === '1';
  }

  function debugIncludes(target) {
    return state.debugEnabled && (state.debugScenario === 'both' || state.debugScenario === target);
  }

  function storageGet() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (_error) {
      return memoryStorage;
    }
  }

  function storageSet(value) {
    memoryStorage = value;
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
      return true;
    } catch (_error) {
      return false;
    }
  }

  function storageRemove() {
    memoryStorage = null;
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (_error) {
      // The in-memory fallback has already been cleared.
    }
  }

  function sanitizeDocumentData(type, data = {}) {
    if (!DOCUMENT_RULES[type]?.api || data.fuente === 'customer_declared') {
      return {
        documentNumber: sanitizeDeclaredDocument(data.documentNumber),
        fuente: 'customer_declared',
      };
    }
    const common = {
      direccion: normalizeAddress(data.direccion),
      fuente: String(data.fuente || 'consulta_peru_api'),
    };
    if (type === 'ruc') {
      const detail = data.direccionDetalle && typeof data.direccionDetalle === 'object'
        ? data.direccionDetalle
        : {};
      return {
        ...common,
        ruc: String(data.ruc || ''),
        razonSocial: String(data.razonSocial || ''),
        estado: String(data.estado || ''),
        condicion: String(data.condicion || ''),
        ubigeo: String(data.ubigeo || ''),
        direccionDetalle: {
          roadType: String(detail.roadType || ''),
          roadName: String(detail.roadName || ''),
          number: String(detail.number || ''),
          district: String(detail.district || ''),
          province: String(detail.province || ''),
          department: String(detail.department || ''),
        },
      };
    }
    return {
      ...common,
      dni: String(data.dni || ''),
      fullName: String(data.fullName || ''),
      nombres: String(data.nombres || ''),
      apellidoPaterno: String(data.apellidoPaterno || ''),
      apellidoMaterno: String(data.apellidoMaterno || ''),
    };
  }

  function isStoredRecordValid(record) {
    if (!record || ![LEGACY_STORAGE_VERSION, 2, STORAGE_VERSION].includes(record.version) || !record.document || !record.location) return false;
    const type = DOCUMENT_RULES[record.document.type] ? record.document.type : 'dni';
    const number = sanitizeDocumentNumber(type, record.document.number);
    const data = sanitizeDocumentData(type, record.document.data || {});
    const rule = DOCUMENT_RULES[type];
    const apiIdentity = rule.api && (type === 'ruc' ? Boolean(data.ruc || data.razonSocial) : Boolean(data.dni || data.fullName));
    const declaredIdentity = record.document.declared === true && (!data.documentNumber || data.documentNumber === number);
    const hasIdentity = rule.api ? (apiIdentity || declaredIdentity || !isDocumentApiEnabled()) : declaredIdentity;
    const statusValid = rule.api
      ? (record.document.validated !== false || record.document.declared === true || !isDocumentApiEnabled())
      : record.document.declared === true;
    const locationValid = !isUbigeoEnabled() || (
      Boolean(record.location.department)
      && Boolean(record.location.province)
      && Boolean(record.location.district)
      && Boolean(record.location.ubigeo)
    );
    return statusValid
      && hasIdentity
      && number.length >= rule.min
      && number.length <= rule.max
      && (rule.api || /^[A-Z0-9]+$/.test(number))
      && locationValid;
  }

  function migrateStoredRecord(record) {
    const migrated = {
      ...record,
      version: STORAGE_VERSION,
      document: {
        ...record.document,
        validated: usesDocumentApi(record.document.type) && record.document.validated !== false,
        declared: !usesDocumentApi(record.document.type) || record.document.declared === true,
      },
    };
    delete migrated.expiresAt;
    return migrated;
  }

  function readStoredRecord() {
    const raw = storageGet();
    if (!raw) return null;
    try {
      const record = JSON.parse(raw);
      if (!isStoredRecordValid(record)) {
        storageRemove();
        return null;
      }
      const migrated = migrateStoredRecord(record);
      if (record.version !== STORAGE_VERSION || Object.prototype.hasOwnProperty.call(record, 'expiresAt')) {
        storageSet(JSON.stringify(migrated));
      }
      return migrated;
    } catch (_error) {
      storageRemove();
      return null;
    }
  }

  function buildStoredRecord() {
    const type = getDocumentType();
    const number = sanitizeDocumentNumber(type, elements.docNumber?.value);
    const payload = state.documentPayload || {};
    const location = state.locationSelection || {};
    return {
      version: STORAGE_VERSION,
      savedAt: Date.now(),
      document: {
        type,
        number,
        validated: state.documentValidated,
        declared: state.documentDeclared,
        providerCode: state.documentProviderCode || payload.providerCode || '',
        providerMessage: state.documentProviderMessage || payload.message || '',
        data: sanitizeDocumentData(type, payload.data || {}),
      },
      location: {
        department: String(location.department || ''),
        province: String(location.province || ''),
        district: String(location.district || ''),
        ubigeo: String(location.ubigeo || ''),
        ineiCode: String(location.ineiCode || ''),
        providerId: String(location.providerId || ''),
        source: String(location.source || state.locationCatalogSource || ''),
        fallback: Boolean(location.fallback),
        fallbackReason: String(location.fallbackReason || ''),
      },
    };
  }

  function sameStoredData(left, right) {
    if (!left || !right) return false;
    return JSON.stringify({ document: left.document, location: left.location })
      === JSON.stringify({ document: right.document, location: right.location });
  }

  function saveCurrentRecord() {
    const hasResolvedLocation = !isUbigeoEnabled() || (state.locationMode === 'select' && state.locationSelection?.ubigeo);
    if ((!state.documentValidated && !state.documentDeclared) || !hasResolvedLocation || state.debugEnabled) return;
    const record = buildStoredRecord();
    if (sameStoredData(state.storedRecord, record)) return;
    state.storedRecord = record;
    const persisted = storageSet(JSON.stringify(record));
    if (!persisted) {
      setResult(elements.summaryResult, 'idle', `<p>${escapeHtml(i18n.storageError)}</p>`);
    }
  }

  function restoreStoredDocument(type, number) {
    const record = state.storedRecord;
    if (!record || getDocumentKey(type, number) !== getDocumentKey(record.document.type, record.document.number)) return false;
    state.documentKey = getDocumentKey(type, number);
    const apiEnabled = usesDocumentApi(type) && record.document.validated !== false && record.document.declared !== true;
    state.documentPayload = {
      ok: apiEnabled,
      type,
      number,
      message: record.document.providerMessage || '',
      providerCode: record.document.providerCode || '',
      data: sanitizeDocumentData(type, record.document.data || {}),
    };
    state.documentValidated = apiEnabled;
    state.documentDeclared = !apiEnabled;
    state.documentFallbackAllowed = false;
    state.documentFallbackReason = '';
    state.documentProviderCode = record.document.providerCode || '';
    state.documentProviderMessage = record.document.providerMessage || '';
    setResult(elements.documentResult, 'success', formatDocumentResult(state.documentPayload));
    return true;
  }

  function hydrateStoredRecord(record) {
    const type = DOCUMENT_RULES[record.document.type] ? record.document.type : 'dni';
    const number = sanitizeDocumentNumber(type, record.document.number);
    elements.docType.value = type;
    elements.docNumber.value = number;
    updateDocumentInput();
    state.storedRecord = record;
    restoreStoredDocument(type, number);
    if (isUbigeoEnabled()) {
      state.locationSelection = { ...record.location };
      state.locationMode = 'select';
      state.locationFallbackReason = '';
      state.locationCatalogSource = record.location.source || '';
    } else {
      disableLocationSection();
    }
    renderSummary();
  }

  function resetDocument(message = '') {
    state.documentKey = '';
    state.documentPayload = null;
    state.documentValidated = false;
    state.documentDeclared = false;
    state.documentFallbackAllowed = false;
    state.documentFallbackReason = '';
    state.documentProviderCode = '';
    state.documentProviderMessage = '';
    setResult(elements.documentResult, message ? 'idle' : '', message ? `<p>${escapeHtml(message)}</p>` : '');
  }

  function updateDocumentInput() {
    const type = getDocumentType();
    const rule = DOCUMENT_RULES[type];
    const labels = {
      dni: { number: i18n.numberDni, placeholder: i18n.enterDni },
      ruc: { number: i18n.numberRuc, placeholder: i18n.enterRuc },
      ce: { number: i18n.numberCE, placeholder: i18n.enterCE },
      ptp: { number: i18n.numberPTP, placeholder: i18n.enterPTP },
    };
    if (!elements.docNumber) return;
    elements.docNumber.maxLength = rule.max;
    elements.docNumber.inputMode = rule.inputMode;
    elements.docNumber.placeholder = labels[type].placeholder || '';
    if (elements.docNumberLabel) elements.docNumberLabel.textContent = labels[type].number || type.toUpperCase();
    if (elements.validateButton) {
      elements.validateButton.hidden = !usesDocumentApi(type);
      elements.validateButton.textContent = interpolate(i18n.validateType, { type: type.toUpperCase() });
    }
  }

  function formatDocumentResult(payload) {
    const data = payload.data || {};
    const address = normalizeAddress(data.direccion);
    if (!usesDocumentApi(payload.type) || isDeclaredPayload(payload)) {
      return `<p><strong>${escapeHtml(i18n.declared)}:</strong> ${escapeHtml(payload.type.toUpperCase())} ${escapeHtml(payload.number)}</p>`;
    }
    if (payload.type === 'ruc') {
      return [
        `<p><strong>${escapeHtml(i18n.valid)}:</strong> RUC ${escapeHtml(data.ruc || payload.number)}</p>`,
        `<p><strong>${escapeHtml(i18n.businessName)}:</strong> ${escapeHtml(data.razonSocial || '-')}</p>`,
        `<p><strong>${escapeHtml(i18n.taxStatus)}:</strong> ${escapeHtml(data.estado || '-')}</p>`,
        `<p><strong>${escapeHtml(i18n.taxCondition)}:</strong> ${escapeHtml(data.condicion || '-')}</p>`,
        address ? `<p><strong>${escapeHtml(i18n.address)}:</strong> ${escapeHtml(address)}</p>` : '',
        data.ubigeo ? `<p><strong>${escapeHtml(i18n.fiscalUbigeo)}:</strong> ${escapeHtml(data.ubigeo)}</p>` : '',
      ].join('');
    }
    return [
      `<p><strong>${escapeHtml(i18n.valid)}:</strong> DNI ${escapeHtml(data.dni || payload.number)}</p>`,
      `<p><strong>${escapeHtml(i18n.name)}:</strong> ${escapeHtml(data.fullName || '-')}</p>`,
      address ? `<p><strong>${escapeHtml(i18n.address)}:</strong> ${escapeHtml(address)}</p>` : '',
    ].join('');
  }

  function fallbackDisplayMessage(reason, provider = {}) {
    if (provider.message) return provider.message;
    if (reason === 'provider_401') return i18n.unauthorizedFallback;
    if (reason === 'provider_403') return i18n.forbiddenFallback;
    if (reason === 'provider_429') return i18n.rateLimitedFallback;
    return i18n.serviceUnavailable;
  }

  function fallbackOrderReason(reason, type) {
    if (!DOCUMENT_RULES[type]?.api) return 'Documento declarado por el cliente sin validacion API';
    if (reason === 'provider_401') return 'Problema de autorizacion del servicio de validacion (401).';
    if (reason === 'provider_403') return 'Acceso rechazado por el servicio de validacion (403).';
    if (reason === 'provider_429') return 'Limite de consultas o cuota alcanzada en el servicio de validacion (429).';
    if (reason === 'provider_5xx') return 'Error de servidor del servicio de validacion.';
    if (reason === 'timeout') return 'Tiempo de espera agotado al consultar el servicio de validacion.';
    if (reason === 'network_error') return 'Error de red al consultar el servicio de validacion.';
    if (reason === 'debug_document_unavailable') return 'Fallback de debug: servicio documental simulado como no disponible.';
    return 'El documento no fue validado por una contingencia del servicio de validacion.';
  }

  function isOperationalFallbackStatus(status) {
    return status === 401 || status === 403 || status === 429;
  }

  function operationalFallbackReason(status) {
    if (status === 401) return 'provider_401';
    if (status === 403) return 'provider_403';
    if (status === 429) return 'provider_429';
    return 'provider_unavailable';
  }

  function operationalFallbackMessage(status) {
    if (status === 401) return i18n.unauthorizedFallback;
    if (status === 403) return i18n.forbiddenFallback;
    if (status === 429) return i18n.rateLimitedFallback;
    return i18n.serviceUnavailable;
  }

  function allowDocumentFallback(type, number, reason, provider = {}, preservedPayload = null) {
    state.documentKey = getDocumentKey(type, number);
    state.documentPayload = preservedPayload;
    state.documentValidated = false;
    state.documentDeclared = false;
    state.documentFallbackAllowed = true;
    state.documentFallbackReason = reason;
    state.documentProviderCode = provider.code || '';
    state.documentProviderMessage = provider.message || '';
    const message = fallbackDisplayMessage(reason, provider);
    setResult(
      elements.documentResult,
      'fallback',
      `<p>${escapeHtml(message)}</p><p><strong>${escapeHtml(i18n.fallbackAllowed)}</strong></p>`
    );
  }

  function functionalErrorMessage(status, fallback) {
    if (status === 401) return i18n.unauthorized;
    if (status === 403) return i18n.forbidden;
    if (status === 404) return i18n.notFound;
    if (status === 429) return i18n.rateLimited;
    return fallback || i18n.notFound;
  }

  async function requestDocument(type, number) {
    try {
      const baseUrl = wrapper.dataset.apiBaseUrl.replace(/\/$/, '');
      const response = await fetchWithTimeout(`${baseUrl}/api/validate?${new URLSearchParams({ type, number })}`);
      let payload = null;
      try { payload = await response.json(); } catch (_error) { payload = null; }
      if (response.ok && payload?.ok === true) return { kind: 'success', payload };
      if (isOperationalFallbackStatus(response.status)) {
        return {
          kind: 'technical',
          reason: operationalFallbackReason(response.status),
          code: payload?.providerCode || String(response.status),
          message: operationalFallbackMessage(response.status),
        };
      }
      if (response.status >= 500) {
        return {
          kind: 'technical',
          reason: 'provider_5xx',
          code: payload?.providerCode || String(response.status),
          message: payload?.message || '',
        };
      }
      return {
        kind: 'functional',
        status: response.status,
        message: functionalErrorMessage(response.status, payload?.message),
      };
    } catch (error) {
      return {
        kind: 'technical',
        reason: error?.name === 'AbortError' ? 'timeout' : 'network_error',
        code: '',
        message: '',
      };
    }
  }

  async function validateDocument() {
    const type = getDocumentType();
    const rule = DOCUMENT_RULES[type];
    const usesApi = usesDocumentApi(type);
    const number = sanitizeDocumentNumber(type, elements.docNumber?.value);
    elements.docNumber.value = number;

    if (number.length < rule.min || number.length > rule.max || (!rule.api && !/^[A-Z0-9]+$/.test(number))) {
      resetDocument();
      const message = rule.min === rule.max
        ? interpolate(i18n.invalidLength, { type: type.toUpperCase(), length: rule.max })
        : interpolate(i18n.invalidDeclared, { min: rule.min, max: rule.max });
      setResult(elements.documentResult, 'error', `<p>${escapeHtml(message)}</p>`);
      return false;
    }

    if (!usesApi) {
      state.documentKey = getDocumentKey(type, number);
      state.documentPayload = {
        ok: false,
        type,
        number,
        message: i18n.declared,
        providerCode: '',
        data: { documentNumber: number, fuente: 'customer_declared' },
      };
      state.documentValidated = false;
      state.documentDeclared = true;
      state.documentFallbackAllowed = false;
      state.documentFallbackReason = '';
      state.documentProviderCode = '';
      state.documentProviderMessage = '';
      setResult(elements.documentResult, 'success', formatDocumentResult(state.documentPayload));
      return true;
    }

    if (debugIncludes('document')) {
      allowDocumentFallback(type, number, 'debug_document_unavailable');
      return true;
    }

    setResult(elements.documentResult, 'loading', `<p>${escapeHtml(interpolate(i18n.validating, { type: type.toUpperCase() }))}</p>`);
    const result = await requestDocument(type, number);

    if (result.kind === 'success') {
      state.documentKey = getDocumentKey(type, number);
      state.documentPayload = result.payload;
      state.documentValidated = true;
      state.documentDeclared = false;
      state.documentFallbackAllowed = false;
      state.documentFallbackReason = '';
      state.documentProviderCode = result.payload.providerCode || '';
      state.documentProviderMessage = result.payload.message || '';
      setResult(elements.documentResult, 'success', formatDocumentResult(result.payload));
      return true;
    }
    if (result.kind === 'technical') {
      allowDocumentFallback(type, number, result.reason, { code: result.code, message: result.message });
      return true;
    }
    resetDocument();
    setResult(elements.documentResult, 'error', `<p>${escapeHtml(result.message)}</p>`);
    return false;
  }

  async function handleDocumentValidation() {
    const type = getDocumentType();
    if (state.busy || !usesDocumentApi(type)) return;
    const number = sanitizeDocumentNumber(type, elements.docNumber?.value);
    const currentKey = getDocumentKey(type, number);
    if (state.documentKey === currentKey && (state.documentValidated || state.documentFallbackAllowed)) return;
    setBusy(true);
    try {
      await validateDocument();
    } finally {
      setBusy(false);
    }
  }

  function setOptions(select, items, placeholder) {
    if (!select) return;
    select.innerHTML = '';
    const firstOption = document.createElement('option');
    firstOption.value = '';
    firstOption.textContent = placeholder;
    select.appendChild(firstOption);
    items.forEach((item, index) => {
      const option = document.createElement('option');
      option.value = String(index);
      option.textContent = item.name;
      select.appendChild(option);
    });
    select.disabled = items.length === 0;
  }

  function hideStoredLocation() {
    if (!elements.locationSaved) return;
    elements.locationSaved.hidden = true;
    if (elements.locationSavedText) elements.locationSavedText.textContent = '';
  }

  function hasCompleteLocation(location) {
    return Boolean(
      location?.department
      && location?.province
      && location?.district
      && location?.ubigeo
    );
  }

  function showStoredLocation(location) {
    if (!hasCompleteLocation(location)) {
      hideStoredLocation();
      return false;
    }
    state.locationMode = 'select';
    state.locationSelection = { ...location };
    elements.locationSelects.hidden = true;
    elements.locationSaved.hidden = false;
    elements.locationSavedText.textContent = [location.department, location.province, location.district].filter(Boolean).join(' / ');
    elements.ubigeo.textContent = location.ubigeo || '-';
    setResult(elements.locationResult, 'success', `<p><strong>${escapeHtml(i18n.locationValid)}</strong></p>`);
    return true;
  }

  function disableLocationSection() {
    hideStoredLocation();
    state.locationMode = 'disabled';
    state.locationSelection = null;
    state.locationCatalog = [];
    state.locationCatalogSource = '';
    state.locationFallbackReason = '';
    if (elements.locationContainer) elements.locationContainer.hidden = true;
    if (elements.summaryLocationCard) elements.summaryLocationCard.hidden = true;
    if (elements.locationSelects) elements.locationSelects.hidden = true;
    if (elements.ubigeo) elements.ubigeo.textContent = '-';
    setResult(elements.locationResult, '', '');
  }

  function showLocationUnavailable(reason = 'ubigeo_services_unavailable') {
    if (elements.locationContainer) elements.locationContainer.hidden = false;
    hideStoredLocation();
    state.locationMode = 'unavailable';
    state.locationFallbackReason = reason;
    state.locationSelection = null;
    state.locationCatalog = [];
    state.locationCatalogSource = '';
    elements.locationSelects.hidden = true;
    elements.ubigeo.textContent = '-';
    setResult(elements.locationResult, 'fallback', `<p>${escapeHtml(i18n.locationUnavailable)}</p>`);
  }

  async function fetchLocationCatalog() {
    const baseUrl = wrapper.dataset.apiBaseUrl.replace(/\/$/, '');
    const response = await fetchWithTimeout(`${baseUrl}/api/ubigeos`);
    let payload = null;
    try { payload = await response.json(); } catch (_error) { payload = null; }
    const departments = payload?.data?.departments;
    if (!response.ok || payload?.ok !== true || !Array.isArray(departments) || !departments.length) {
      throw new Error(response.status >= 500 ? 'provider_5xx' : 'location_catalog_error');
    }
    return departments;
  }

  function getLocalLocationCatalog() {
    const departments = window.KoreanCosmeticsUbigeoCatalog?.departments;
    if (!Array.isArray(departments) || !departments.length) {
      throw new Error('local_catalog_unavailable');
    }
    return departments;
  }

  function findCatalogLocation(preferred) {
    if (!preferred) return null;
    const departmentIndex = state.locationCatalog.findIndex((item) => item.name === preferred.department);
    const department = state.locationCatalog[departmentIndex];
    if (!department) return null;
    const provinceIndex = (department.provinces || []).findIndex((item) => item.name === preferred.province);
    const province = department.provinces?.[provinceIndex];
    if (!province) return null;
    const districtIndex = (province.districts || []).findIndex((item) => (
      preferred.ubigeo && item.code === preferred.ubigeo
    ) || item.name === preferred.district);
    const district = province.districts?.[districtIndex];
    if (!district) return null;
    return { departmentIndex, provinceIndex, districtIndex, department, province, district };
  }

  function applyCatalogLocation(preferred) {
    const match = findCatalogLocation(preferred);
    if (!match) return false;
    elements.department.value = String(match.departmentIndex);
    setOptions(elements.province, match.department.provinces || [], i18n.selectProvince);
    elements.province.value = String(match.provinceIndex);
    setOptions(elements.district, match.province.districts || [], i18n.selectDistrict);
    elements.district.value = String(match.districtIndex);
    state.locationSelection = {
      department: match.department.name,
      province: match.province.name,
      district: match.district.name,
      ubigeo: match.district.code,
      ineiCode: match.district.ineiCode || '',
      providerId: match.district.id === null || match.district.id === undefined ? '' : String(match.district.id),
      source: state.locationCatalogSource,
      fallback: state.locationCatalogSource === 'theme_catalog',
      fallbackReason: state.locationCatalogSource === 'theme_catalog' ? state.locationFallbackReason : '',
    };
    elements.ubigeo.textContent = match.district.code;
    setResult(elements.locationResult, 'success', `<p><strong>${escapeHtml(i18n.locationValid)}</strong></p><p>${escapeHtml(match.department.name)} / ${escapeHtml(match.province.name)} / ${escapeHtml(match.district.name)}</p>`);
    return true;
  }

  async function loadLocations(preferred = null) {
    if (!isUbigeoEnabled()) {
      disableLocationSection();
      return true;
    }
    if (elements.locationContainer) elements.locationContainer.hidden = false;
    const requestId = ++state.locationRequestId;
    hideStoredLocation();
    state.locationMode = 'loading';
    state.locationFallbackReason = '';
    state.locationCatalogSource = '';
    elements.locationSelects.hidden = false;
    elements.ubigeo.textContent = '-';
    setOptions(elements.department, [], i18n.loadingLocations);
    setOptions(elements.province, [], i18n.selectProvince);
    setOptions(elements.district, [], i18n.selectDistrict);
    setResult(elements.locationResult, 'loading', `<p>${escapeHtml(i18n.loadingLocations)}</p>`);

    try {
      let departments = null;
      let source = 'worker_api';
      if (debugIncludes('ubigeo')) {
        departments = getLocalLocationCatalog();
        source = 'theme_catalog';
      } else {
        try {
          departments = await fetchLocationCatalog();
        } catch (_remoteError) {
          departments = getLocalLocationCatalog();
          source = 'theme_catalog';
        }
      }
      if (requestId !== state.locationRequestId) return false;
      state.locationMode = 'select';
      state.locationCatalog = departments;
      state.locationCatalogSource = source;
      state.locationFallbackReason = source === 'theme_catalog'
        ? (debugIncludes('ubigeo') ? 'debug_ubigeo_unavailable' : 'worker_ubigeo_unavailable')
        : '';
      state.locationSelection = null;
      setOptions(elements.department, departments, i18n.selectDepartment);
      setOptions(elements.province, [], i18n.selectProvince);
      setOptions(elements.district, [], i18n.selectDistrict);
      if (preferred && applyCatalogLocation(preferred)) return true;
      const message = source === 'theme_catalog' ? i18n.localCatalogActive : i18n.selectLocation;
      setResult(elements.locationResult, source === 'theme_catalog' ? 'fallback' : 'idle', `<p>${escapeHtml(message)}</p>`);
      return !preferred;
    } catch (_error) {
      if (requestId !== state.locationRequestId) return false;
      showLocationUnavailable();
      return true;
    }
  }

  function selectedLocation() {
    const department = elements.department.value === '' ? null : state.locationCatalog[Number(elements.department.value)];
    const province = elements.province.value === '' ? null : department?.provinces?.[Number(elements.province.value)];
    const district = elements.district.value === '' ? null : province?.districts?.[Number(elements.district.value)];
    return { department, province, district };
  }

  function changeDepartment() {
    const { department } = selectedLocation();
    state.locationSelection = null;
    elements.ubigeo.textContent = '-';
    setOptions(elements.province, department?.provinces || [], i18n.selectProvince);
    setOptions(elements.district, [], i18n.selectDistrict);
    setResult(elements.locationResult, 'idle', `<p>${escapeHtml(i18n.selectLocation)}</p>`);
  }

  function changeProvince() {
    const { province } = selectedLocation();
    state.locationSelection = null;
    elements.ubigeo.textContent = '-';
    setOptions(elements.district, province?.districts || [], i18n.selectDistrict);
    setResult(elements.locationResult, 'idle', `<p>${escapeHtml(i18n.selectLocation)}</p>`);
  }

  function changeDistrict() {
    const { department, province, district } = selectedLocation();
    if (!department || !province || !district) {
      state.locationSelection = null;
      elements.ubigeo.textContent = '-';
      setResult(elements.locationResult, 'idle', `<p>${escapeHtml(i18n.selectLocation)}</p>`);
      return;
    }
    state.locationSelection = {
      department: department.name,
      province: province.name,
      district: district.name,
      ubigeo: district.code,
      ineiCode: district.ineiCode || '',
      providerId: district.id === null || district.id === undefined ? '' : String(district.id),
      source: state.locationCatalogSource,
      fallback: state.locationCatalogSource === 'theme_catalog',
      fallbackReason: state.locationCatalogSource === 'theme_catalog' ? state.locationFallbackReason : '',
    };
    elements.ubigeo.textContent = district.code;
    setResult(elements.locationResult, 'success', `<p><strong>${escapeHtml(i18n.locationValid)}</strong></p><p>${escapeHtml(department.name)} / ${escapeHtml(province.name)} / ${escapeHtml(district.name)}</p>`);
  }

  function documentAttributes() {
    const type = getDocumentType();
    const number = sanitizeDocumentNumber(type, elements.docNumber?.value);
    const validated = usesDocumentApi(type) && state.documentValidated ? 'true' : 'false';
    const reason = validated === 'true'
      ? ''
      : (state.documentDeclared || !usesDocumentApi(type) ? 'Documento declarado por el cliente sin validacion API' : fallbackOrderReason(state.documentFallbackReason, type));
    return {
      document_type: type,
      document_number: number,
      document_validated: validated,
      document_unvalidated_reason: reason,
    };
  }
  function locationAttributes() {
    const location = state.locationSelection || {};
    if (!isUbigeoEnabled() || state.locationMode === 'unavailable' || !hasCompleteLocation(location)) return {};
    return {
      location_department: location.department,
      location_province: location.province,
      location_district: location.district,
      location_ubigeo: location.ubigeo,
    };
  }

  function orderAttributes(values = {}) {
    const attributes = Object.entries(ACTIVE_ATTRIBUTE_NAMES).reduce((result, [key, name]) => {
      result[name] = values[key] || '';
      return result;
    }, {});

    LEGACY_ATTRIBUTE_KEYS.forEach((key) => {
      attributes[`__${key}`] = '';
    });
    return attributes;
  }

  function currentAttributes() {
    return orderAttributes({ ...documentAttributes(), ...locationAttributes() });
  }

  function emptyAttributes() {
    return orderAttributes({});
  }

  function injectHiddenAttributes(form, attributes) {
    form.querySelectorAll('[data-identity-cart-attribute]').forEach((input) => input.remove());
    Object.entries(attributes).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `attributes[${key}]`;
      input.value = value || '';
      input.setAttribute('data-identity-cart-attribute', '');
      form.appendChild(input);
    });
  }

  async function updateCartAttributes(attributes) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    if (!response.ok) throw new Error('cart_update_failed');
    return response.json();
  }

  function isDocumentReady() {
    const type = getDocumentType();
    const number = sanitizeDocumentNumber(type, elements.docNumber?.value);
    return state.documentKey === getDocumentKey(type, number)
      && (state.documentValidated || state.documentDeclared || state.documentFallbackAllowed);
  }

  function isLocationReady() {
    if (!isUbigeoEnabled()) return true;
    if (state.locationMode === 'select') return Boolean(state.locationSelection);
    if (state.locationMode === 'unavailable') return true;
    return false;
  }

  function validateFormState() {
    if (!isDocumentReady()) {
      setResult(elements.documentResult, 'error', `<p>${escapeHtml(i18n.validateFirst)}</p>`);
      showForm();
      return false;
    }
    if (!isLocationReady()) {
      setResult(elements.locationResult, 'error', `<p>${escapeHtml(i18n.selectLocation)}</p>`);
      showForm();
      return false;
    }
    return true;
  }

  function renderSummary() {
    const type = getDocumentType();
    const number = sanitizeDocumentNumber(type, elements.docNumber?.value);
    const data = state.documentPayload?.data || {};
    const address = normalizeAddress(data.direccion);
    const location = state.locationSelection || {};
    const primaryName = type === 'dni' ? data.nombres || data.fullName || '' : (type === 'ruc' ? data.razonSocial || '' : '');
    if (elements.summaryLocationCard) elements.summaryLocationCard.hidden = !isUbigeoEnabled();
    const summaryLabels = {
      dni: i18n.summaryDni,
      ruc: i18n.summaryRuc,
      ce: i18n.summaryCE,
      ptp: i18n.summaryPTP,
    };
    elements.summaryGreeting.textContent = primaryName ? interpolate(i18n.summaryGreeting, { name: primaryName }) : i18n.summaryTitle;
    elements.summaryDocumentTitle.textContent = interpolate(summaryLabels[type] || `${type.toUpperCase()} {{ number }}`, { number });
    const documentIsDeclared = state.documentDeclared && !state.documentValidated;
    elements.summaryDocumentDetail.textContent = documentIsDeclared
      ? i18n.declared
      : (type === 'dni'
        ? data.fullName || '-'
        : (type === 'ruc' ? [data.razonSocial, data.estado, data.condicion].filter(Boolean).join(' | ') || '-' : i18n.declared));
    elements.summaryAddressRow.hidden = !address;
    elements.summaryAddress.textContent = address;
    if (isUbigeoEnabled()) {
      elements.summaryLocation.textContent = state.locationMode === 'unavailable'
        ? i18n.locationUnavailable
        : [location.department, location.province, location.district].filter(Boolean).join(' / ') || '-';
      elements.summaryUbigeo.textContent = location.ubigeo || '-';
    }
  }

  function showSummary() {
    state.view = 'summary';
    renderSummary();
    elements.summary.hidden = false;
    elements.form.hidden = true;
    setResult(elements.summaryResult, '', '');
  }

  async function showForm() {
    state.view = 'form';
    elements.summary.hidden = true;
    elements.form.hidden = false;
    hideStoredLocation();

    if (!isUbigeoEnabled()) {
      disableLocationSection();
    } else if (state.locationMode === 'unavailable') {
      showLocationUnavailable();
    } else if (state.locationSelection && state.locationCatalog.length) {
      hideStoredLocation();
      elements.locationSelects.hidden = false;
      applyCatalogLocation(state.locationSelection);
    } else if (hasCompleteLocation(state.locationSelection)) {
      showStoredLocation(state.locationSelection);
    } else if (state.locationMode !== 'loading') {
      await loadLocations();
    }

    requestAnimationFrame(() => elements.docType?.focus());
  }

  function openModal(button) {
    state.activeButton = button;
    state.activeForm = button.form;
    state.lastFocused = button;
    const stored = readStoredRecord();
    if (stored) {
      hydrateStoredRecord(stored);
      showSummary();
    } else {
      state.storedRecord = null;
      resetDocument();
      state.locationSelection = null;
      state.locationMode = isUbigeoEnabled() ? 'idle' : 'disabled';
      elements.docType.value = 'dni';
      elements.docNumber.value = '';
      updateDocumentInput();
      showForm();
    }
    document.body.classList.add('cart-identity-modal-open');
    if (!elements.modal.open) elements.modal.showModal();
  }

  function closeModal() {
    if (elements.modal.open) elements.modal.close();
    document.body.classList.remove('cart-identity-modal-open');
    state.lastFocused?.focus?.();
  }

  async function continueToCheckout() {
    if (state.busy || !state.activeForm || !state.activeButton) return;
    setBusy(true);

    if (!isDocumentReady()) {
      const rule = DOCUMENT_RULES[getDocumentType()];
      if (usesDocumentApi(getDocumentType())) {
        setResult(elements.documentResult, 'error', `<p>${escapeHtml(i18n.validateFirst)}</p>`);
        setBusy(false);
        await showForm();
        return;
      }

      const documentReady = await validateDocument();
      if (!documentReady) {
        setBusy(false);
        await showForm();
        return;
      }
    }

    if (!validateFormState()) {
      setBusy(false);
      return;
    }

    const resultTarget = state.view === 'summary' ? elements.summaryResult : elements.documentResult;
    setResult(resultTarget, 'loading', `<p>${escapeHtml(i18n.saving)}</p>`);

    const attributes = currentAttributes();
    injectHiddenAttributes(state.activeForm, attributes);
    try {
      await updateCartAttributes(attributes);
    } catch (_error) {
      // Hidden fields remain as a native-submit fallback.
    }

    if ((state.documentValidated || state.documentDeclared) && (!isUbigeoEnabled() || state.locationMode === 'select') && !state.debugEnabled) {
      saveCurrentRecord();
    }

    const form = state.activeForm;
    const button = state.activeButton;
    state.bypassForm = form;
    setResult(resultTarget, '', '');
    closeModal();
    setBusy(false);
    form.requestSubmit(button);
  }

  async function forgetData() {
    if (state.busy) return;
    setBusy(true);
    storageRemove();
    state.storedRecord = null;
    try {
      await updateCartAttributes(emptyAttributes());
    } catch (_error) {
      // The next successful checkout will overwrite all known attributes.
    }
    elements.docType.value = 'dni';
    elements.docNumber.value = '';
    updateDocumentInput();
    resetDocument();
    state.locationSelection = null;
    state.locationMode = isUbigeoEnabled() ? 'idle' : 'disabled';
    setBusy(false);
    await showForm();
  }

  function applyDebugConfiguration() {
    state.debugEnabled = Boolean(isDebugAvailable() && elements.debugEnabled?.checked);
    state.debugScenario = ['document', 'ubigeo', 'both'].includes(elements.debugScenario?.value)
      ? elements.debugScenario.value
      : 'both';
    if (elements.debugScenario) elements.debugScenario.disabled = !state.debugEnabled;
    resetDocument(i18n.documentChanged);
    state.locationRequestId += 1;
    state.locationSelection = null;
    state.locationFallbackReason = '';
    state.locationCatalogSource = '';
    if (isUbigeoEnabled()) {
      loadLocations();
    } else {
      disableLocationSection();
    }
  }

  function interceptCheckout(button, event) {
    const form = button.form;
    if (!form || state.bypassForm === form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const cartHelper = window.Alpine?.store?.('xCartHelper');
    if (cartHelper) {
      cartHelper.validateCart(true);
      if (!cartHelper.validated) return;
    }
    openModal(button);
  }

  function initialize() {
    if (state.initialized) return;
    wrapper = document.querySelector('[data-cart-identity-validator]');
    if (!wrapper) return;
    state.initialized = true;
    elements = getElements();
    i18n = parseI18n();
    if (!isUbigeoEnabled()) disableLocationSection();
    if (isDebugAvailable() && elements.debugPanel) elements.debugPanel.hidden = false;
    if (elements.debugEnabled) elements.debugEnabled.checked = false;
    if (elements.debugScenario) {
      elements.debugScenario.value = 'both';
      elements.debugScenario.disabled = true;
    }
    updateDocumentInput();
    setResult(elements.documentResult, '', '');

    elements.modal.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeModal();
    });
    elements.modal.addEventListener('click', (event) => {
      if (event.target === elements.modal) closeModal();
    });
    elements.closeButtons.forEach((button) => button.addEventListener('click', closeModal));
    elements.editButton.addEventListener('click', showForm);
    elements.changeLocationButton?.addEventListener('click', async () => {
      const preferred = state.locationSelection ? { ...state.locationSelection } : null;
      await loadLocations(preferred);
    });
    elements.forgetButtons.forEach((button) => button.addEventListener('click', forgetData));
    elements.continueButtons.forEach((button) => button.addEventListener('click', continueToCheckout));
    elements.validateButton?.addEventListener('click', handleDocumentValidation);
    elements.docNumber.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      event.stopPropagation();
      if (usesDocumentApi(getDocumentType())) handleDocumentValidation();
    });
    elements.docNumber.addEventListener('input', () => {
      const type = getDocumentType();
      elements.docNumber.value = sanitizeDocumentNumber(type, elements.docNumber.value).slice(0, DOCUMENT_RULES[type].max);
      const currentKey = getDocumentKey(type, elements.docNumber.value);
      if (restoreStoredDocument(type, elements.docNumber.value)) return;
      if (state.documentKey !== currentKey) resetDocument();
    });
    elements.docType.addEventListener('change', () => {
      elements.docNumber.value = '';
      updateDocumentInput();
      resetDocument();
    });
    elements.department.addEventListener('change', changeDepartment);
    elements.province.addEventListener('change', changeProvince);
    elements.district.addEventListener('change', changeDistrict);
    elements.debugEnabled?.addEventListener('change', applyDebugConfiguration);
    elements.debugScenario?.addEventListener('change', applyDebugConfiguration);
  }

  document.addEventListener('click', (event) => {
    const checkoutButton = event.target.closest('button[name="checkout"]');
    if (!checkoutButton) return;
    initialize();
    if (!state.initialized) return;
    interceptCheckout(checkoutButton, event);
  }, true);

  document.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') return;
    initialize();
    if (!state.initialized) return;
    if (state.bypassForm === event.target) {
      state.bypassForm = null;
      return;
    }
    interceptCheckout(submitter, event);
  }, true);

  document.addEventListener('DOMContentLoaded', initialize);
})();
